import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './useAuth'
import { useAdmin } from './useAdmin'
import { setCurrentIdentitySnapshot } from './currentIdentity'
import { ImpersonationContext, type ImpersonationContextValue } from './impersonationContext'

const STORAGE_KEY = 'impersonate_identity_id'
const STORAGE_NAME_KEY = 'impersonate_name'
// The operator who started the impersonation. Persisted so a session can't
// resurrect under a different user who logs into the same tab later.
const STORAGE_OWNER_KEY = 'impersonate_owner'

function readStored(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { state: authState } = useAuth()
  const { state: adminState } = useAdmin()

  const [impersonatedIdentityId, setImpersonatedIdentityId] = useState<string | null>(() =>
    readStored(STORAGE_KEY),
  )
  const [impersonatedName, setImpersonatedName] = useState<string | null>(() =>
    readStored(STORAGE_NAME_KEY),
  )
  const [impersonationOwner, setImpersonationOwner] = useState<string | null>(() =>
    readStored(STORAGE_OWNER_KEY),
  )

  const ownIdentityId = authState.status === 'authenticated' ? authState.identityId : null

  const impersonate = useCallback(
    (identityId: string, name?: string) => {
      setImpersonatedIdentityId(identityId)
      setImpersonatedName(name ?? null)
      setImpersonationOwner(ownIdentityId)
      try {
        sessionStorage.setItem(STORAGE_KEY, identityId)
        if (name) sessionStorage.setItem(STORAGE_NAME_KEY, name)
        else sessionStorage.removeItem(STORAGE_NAME_KEY)
        if (ownIdentityId) sessionStorage.setItem(STORAGE_OWNER_KEY, ownIdentityId)
      } catch {
        /* sessionStorage unavailable; in-memory state still applies */
      }
    },
    [ownIdentityId],
  )

  const stopImpersonating = useCallback(() => {
    setImpersonatedIdentityId(null)
    setImpersonatedName(null)
    setImpersonationOwner(null)
    try {
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(STORAGE_NAME_KEY)
      sessionStorage.removeItem(STORAGE_OWNER_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // Only honor impersonation when the operator is currently an admin AND is the
  // same identity that started the session. Otherwise we fall back to the
  // operator's own identity — a non-admin (or a different user in this tab)
  // never inherits a stale impersonation, without us mutating state in an effect.
  const isAdmin = adminState.status === 'admin'
  const ownerMatches = impersonationOwner !== null && impersonationOwner === ownIdentityId
  const effectiveImpersonatedId = isAdmin && ownerMatches ? impersonatedIdentityId : null
  const isImpersonating = effectiveImpersonatedId !== null

  // Keep the module-level snapshot in sync for non-React callers. Done during
  // render (not in an effect) so it is set before any descendant's mount effect
  // runs: child effects fire before parent effects, but parent render runs
  // before child render. The write is idempotent, so StrictMode double-invoke is
  // harmless.
  setCurrentIdentitySnapshot({
    currentIdentityId: effectiveImpersonatedId ?? ownIdentityId,
    impersonatorIdentityId: ownIdentityId,
  })

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      impersonatedIdentityId: effectiveImpersonatedId,
      impersonatedName: isImpersonating ? impersonatedName : null,
      isImpersonating,
      impersonate,
      stopImpersonating,
    }),
    [effectiveImpersonatedId, isImpersonating, impersonatedName, impersonate, stopImpersonating],
  )

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
}
